import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    signal,
    type TemplateRef,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    NavigationEnd,
    Router,
    RouterLink,
    RouterLinkActive,
} from '@angular/router';
import { NgIcon } from '@ng-icons/core';
import { injectOrg } from '@quipex/core/auth';
import {
    AppTopBarScopeRegistry,
    Layout,
    provideAppTopBarScope,
    type AppTopBarModuleMenuContext,
    type AppTopBarScopePortal,
    type AppTopBarShortcut,
} from '@quipex/core/layout';
import {
    createRealtimeJobNotificationToasts,
    RealtimeService,
} from '@quipex/core/realtime';
import { qpxToast as toast } from '@quipex/core/utils';
import {
    injectActivityLauncher,
    isActivityJobProgressMessage,
} from '@quipex/feature/app-activity';
import type { AppQuippyScopeSelection } from '@quipex/feature/app-quippy';
import { injectAppQuippyDialog } from '@quipex/feature/app-quippy/dialog';
import {
    injectWhatsNewDialog,
    provideWhatsNew,
} from '@quipex/feature/app-whats-new';
import { injectBuildingSelectorLauncher } from '@quipex/feature/buildings-find';
import { injectManageOrganisationUsersEntry } from '@quipex/feature/organisations/manage-organisation-users-entry';
import { type NavButton, type NavItem, QpxIcon } from '@quipex/shared/ui';
import { filter } from 'rxjs';

type AppDialogLaunchKind = 'activity' | 'quippy' | 'other';

@Component({
    selector: 'qpx-app-layout',
    imports: [Layout, NgIcon, RouterLink, RouterLinkActive, QpxIcon],
    templateUrl: './app-layout.html',
    providers: [provideWhatsNew()],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppLayout {
    private readonly destroyRef = inject(DestroyRef);
    private readonly layout = viewChild(Layout);
    private readonly activityLauncher = injectActivityLauncher({
        focusDestination: () => this.layout()?.focusRoutedContent(),
        focusFallback: () => this.layout()?.focusUtilityAction('activity'),
    });
    private readonly org = injectOrg();
    private readonly scopeRegistry = inject(AppTopBarScopeRegistry);
    private readonly manageOrganisationUsersEntry =
        injectManageOrganisationUsersEntry();
    private readonly buildingSelector = injectBuildingSelectorLauncher();
    private readonly realtimeService = inject(RealtimeService);
    private readonly router = inject(Router);
    private readonly whatsNewDialog = injectWhatsNewDialog();
    protected readonly hasUnreadReleaseNotes = this.whatsNewDialog.hasUnread;
    private readonly quippyDialog = injectAppQuippyDialog({
        focusFallback: () => this.layout()?.focusUtilityAction('quippy'),
    });
    private readonly appTopBarModuleMenu = viewChild<
        TemplateRef<AppTopBarModuleMenuContext>
    >('appTopBarModuleMenu');
    private appDialogLaunch:
        | {
              readonly kind: AppDialogLaunchKind;
              readonly promise: Promise<unknown>;
          }
        | undefined;
    private readonly currentUrl = signal(this.router.url);
    protected readonly portfolioChatAvailable = computed(() =>
        Boolean(this.org())
    );
    protected readonly extraGeneralNavItems = computed<readonly NavItem[]>(() =>
        this.org()
            ? [
                  {
                      kind: 'link',
                      key: 'portfolio-quippy',
                      label: 'Ask Quippy',
                      icon: 'lucideSearch',
                      route: '/chat',
                      className: 'qpx-nav-button-ai-mode qpx-sparkle',
                  },
              ]
            : []
    );
    protected readonly utilityActions = computed<readonly NavButton[]>(() => {
        const attentionCount = this.activityLauncher.attentionCount();
        const itemLabel = attentionCount === 1 ? 'item' : 'items';
        const badgeDescription =
            attentionCount > 0
                ? `${attentionCount} unseen activity ${itemLabel}`
                : undefined;
        let ariaLabel = 'Open Activity';
        if (attentionCount > 0) {
            ariaLabel = `Activity, ${attentionCount} unseen ${itemLabel}`;
        }

        const activity: NavButton = {
            kind: 'button',
            key: 'activity',
            label: 'Activity',
            animatedIcon: {
                name: 'activity',
                active: this.activityLauncher.hasInProgress(),
            },
            badgeMode: attentionCount > 0 ? 'dot' : undefined,
            badgeDescription,
            ariaHasPopup: 'dialog',
            ariaLabel,
            onClick: () => this.openActivity(),
        };
        const quippySelection = this.currentQuippySelection();
        if (!quippySelection) return [activity];

        return [
            {
                kind: 'button',
                key: 'quippy',
                label: 'Ask Quippy',
                icon: 'lucideSearch',
                className: 'qpx-nav-button-ai-mode qpx-sparkle',
                ariaHasPopup: 'dialog',
                ariaExpanded: this.quippyDialog.isOpen(),
                ariaLabel: this.quippyDialog.isOpen()
                    ? 'Ask Quippy open'
                    : 'Open Ask Quippy',
                onClick: () => this.openQuippy(quippySelection),
            },
            activity,
        ];
    });
    private readonly currentQuippySelection =
        computed<AppQuippyScopeSelection | null>(() => {
            const organisation = this.org();
            const activeScope = this.scopeRegistry.activeScope();
            if (!organisation || !activeScope) return null;

            if (activeScope.kind === 'building') {
                return {
                    kind: 'building',
                    building: activeScope.currentBuilding,
                };
            }

            return {
                kind: 'portfolio',
            };
        });
    protected readonly topBarShortcutsByScope = computed<
        Partial<
            Record<AppTopBarScopePortal['kind'], readonly AppTopBarShortcut[]>
        >
    >(() => {
        const companyUsers: readonly AppTopBarShortcut[] =
            this.manageOrganisationUsersEntry.available()
                ? [
                      {
                          key: 'company-users',
                          label: 'Manage my organisation',
                          description: 'Users, settings and billing',
                          route: this.manageOrganisationUsersEntry.route,
                          icon: 'lucideBriefcaseBusiness',
                      },
                  ]
                : [];

        const portfolio: readonly AppTopBarShortcut[] = [
            {
                key: 'portfolio',
                label: 'My buildings',
                description: 'Find and manage buildings',
                route: '/buildings',
                icon: 'lucideBuilding2',
            },
        ];

        return {
            app: companyUsers,
            building: [...portfolio, ...companyUsers],
            company: portfolio,
        };
    });
    protected readonly manageCompanyShortcut =
        computed<AppTopBarShortcut | null>(() =>
            this.manageOrganisationUsersEntry.available()
                ? {
                      key: 'company-users',
                      label: 'Manage organisation',
                      route: this.manageOrganisationUsersEntry.route,
                      icon: 'lucideBriefcaseBusiness',
                  }
                : null
        );
    private readonly appTopBarScope = computed<AppTopBarScopePortal | null>(
        () => {
            const moduleMenu = this.appTopBarModuleMenu();
            if (!moduleMenu) return null;

            const path = this.currentUrl().split(/[?#]/, 1)[0];
            let moduleTriggerIcon = 'lucideHouse';
            let moduleTriggerLabel = 'Home';
            if (path === '/chat' || path.startsWith('/chat/')) {
                moduleTriggerIcon = 'lucideSearch';
                moduleTriggerLabel = 'Ask Quippy';
            } else if (path === '/buildings') {
                moduleTriggerIcon = 'lucideBuilding2';
                moduleTriggerLabel = 'My buildings';
            }

            return {
                kind: 'app',
                currentBuilding: null,
                moduleMenu,
                moduleMenuAriaLabel: 'App modules',
                moduleTriggerIcon,
                moduleTriggerLabel,
            };
        }
    );

    public constructor() {
        provideAppTopBarScope(this.appTopBarScope);

        afterNextRender(() => this.currentUrl.set(this.router.url));

        void this.realtimeService.connect();
        this.destroyRef.onDestroy(() => void this.realtimeService.disconnect());
        createRealtimeJobNotificationToasts(
            this.realtimeService.jobProgress$.pipe(
                filter((message) => !isActivityJobProgressMessage(message))
            )
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();

        this.router.events
            .pipe(
                filter(
                    (event): event is NavigationEnd =>
                        event instanceof NavigationEnd
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((event) => {
                this.currentUrl.set(event.urlAfterRedirects);
            });
    }

    protected openFindBuilding(origin?: HTMLElement): void {
        this.openAppDialog(
            () => this.buildingSelector.open(origin),
            'Unable to open Find Building. Try again.'
        );
    }

    protected openWhatsNew(): void {
        this.openAppDialog(
            () => this.whatsNewDialog.open(),
            "Unable to open What's new. Try again."
        );
    }

    private openActivity(): void {
        if (this.appDialogLaunch?.kind === 'other') return;

        this.trackAppDialogLaunch('activity', this.activityLauncher.open());
    }

    private openQuippy(selection: AppQuippyScopeSelection): void {
        if (this.appDialogLaunch?.kind === 'other') return;

        this.trackAppDialogLaunch('quippy', this.quippyDialog.open(selection));
    }

    private openAppDialog(
        open: () => Promise<unknown>,
        defaultErrorMessage: string
    ): void {
        if (this.appDialogLaunch) return;

        const launch = open();
        this.trackAppDialogLaunch('other', launch);

        toast.task(() => launch, {
            error: {
                defaultMessage: defaultErrorMessage,
            },
        });
    }

    private trackAppDialogLaunch(
        kind: AppDialogLaunchKind,
        promise: Promise<unknown>
    ): void {
        const launch = { kind, promise } as const;
        this.appDialogLaunch = launch;
        void promise.then(
            () => this.clearAppDialogLaunch(launch),
            () => this.clearAppDialogLaunch(launch)
        );
    }

    private clearAppDialogLaunch(launch: {
        readonly kind: AppDialogLaunchKind;
        readonly promise: Promise<unknown>;
    }): void {
        if (this.appDialogLaunch === launch) {
            this.appDialogLaunch = undefined;
        }
    }
}
